import React from "react";
import PropTypes from "prop-types";
import { FaUser } from "react-icons/fa";
import { RiExternalLinkLine } from "react-icons/ri";
import { SocialIcon } from "react-social-icons";
import clsx from "clsx";
import { useAuth0 } from "@auth0/auth0-react";
import useMixpanelEvent from "../../hooks/useMixpanelEvent";
import { useUser } from "../../context/UserContext";

/**
 * AgentCard component displays agent information with profile picture, name, company, and social media links
 * @param {Object} props - Component props
 * @param {Object} props.agent - Agent data object
 * @param {string} props.agent.name - Agent's full name
 * @param {string} props.agent.company - Agent's company name
 * @param {string} props.agent.profilePicture - URL to agent's profile picture
 * @param {string} props.agent.profileUrl - URL to agent's profile page
 * @param {string} props.agent.cta - Custom call to action text
 * @param {string} props.agent.mlsId - Agent's MLS ID
 * @param {Object} props.agent.socialLinks - Object containing social media URLs
 * @param {string} props.agent.socialLinks.instagram - Instagram profile URL
 * @param {string} props.agent.socialLinks.tiktok - TikTok profile URL
 * @param {string} props.agent.socialLinks.twitter - Twitter/X profile URL
 * @param {string} props.agent.socialLinks.facebook - Facebook profile URL
 * @param {string} props.agent.socialLinks.youtube - YouTube channel URL
 * @param {string} props.clickLocation - The location where the AgentCard is displayed (property_page, daily_score_modal)
 * @returns {JSX.Element} AgentCard component
 */
const AgentCard = ({ agent, clickLocation }) => {
    const { isAuthenticated } = useAuth0();
    const { userState } = useUser();
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();

    const { name, company, profilePicture, profileUrl, cta, mlsId, socialLinks = {} } = agent || {};

    const hasSocialLinks = Object.values(socialLinks).some((link) => link);

    const trackAgentClick = (clickType) => {
        const eventData = {
            isAuthenticated,
            pageUrl: window.location.href,
            user_id: userState?.id || null,
            nickname: userState?.nickname || null,
            email: userState?.email_address || null,
            agentName: name,
            agentMlsId: mlsId,
            clickType,
            clickLocation,
        };

        sendMixpanelEvent("Agent Click", eventData);
    };

    const handleExternalLinkClick = (url) => {
        if (url) {
            trackAgentClick("custom");
            window.open(url, "_blank", "noopener,noreferrer");
        }
    };

    const handleCardClick = (e) => {
        // Don't trigger if clicking on social links or external link button
        if (e.target.closest(".social-icon") || e.target.closest(".external-link-btn")) {
            return;
        }

        if (profileUrl) {
            trackAgentClick("custom");
            window.open(profileUrl, "_blank", "noopener,noreferrer");
        }
    };

    const handleSocialLinkClick = (platform, url) => {
        trackAgentClick(platform);
        if (url) {
            window.open(url, "_blank", "noopener,noreferrer");
        }
    };

    return (
        <div
            className={clsx(
                "tw-bg-white tw-rounded-xl tw-p-5 tw-mx-0 tw-shadow-md tw-w-full tw-font-noto tw-min-h-fit",
                { "tw-cursor-pointer": profileUrl }
            )}
            onClick={handleCardClick}
        >
            {/* Header Section */}
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
                <span className="tw-font-bold tw-text-lg tw-text-gray-800 tw-font-merriweather">
                    Listing courtesy of:
                </span>
                {profileUrl && (
                    <button
                        className="external-link-btn tw-bg-transparent tw-border-none tw-text-gray-500 tw-cursor-pointer tw-p-1 tw-rounded tw-transition-colors hover:tw-text-gray-800"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleExternalLinkClick(profileUrl);
                        }}
                        aria-label="View agent profile"
                    >
                        <RiExternalLinkLine className="tw-w-6 tw-h-6" />
                    </button>
                )}
            </div>

            {/* Profile Information Section */}
            <div className={clsx("tw-flex tw-items-center tw-gap-3", { "tw-mb-4": hasSocialLinks })}>
                <div className="tw-relative tw-w-12 tw-h-12 tw-rounded-full tw-overflow-hidden tw-flex-shrink-0">
                    {profilePicture ? (
                        <img
                            src={profilePicture}
                            alt={`${name} profile`}
                            className="tw-w-full tw-h-full tw-object-cover tw-rounded-full"
                            onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "flex";
                            }}
                        />
                    ) : null}
                    <div
                        className={clsx(
                            "tw-w-full tw-h-full tw-bg-gray-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-gray-500 tw-text-lg",
                            { "tw-hidden": profilePicture }
                        )}
                    >
                        <FaUser />
                    </div>
                </div>
                <div className="tw-flex-1 tw-min-w-0">
                    <h3 className="tw-text-lg tw-font-bold tw-text-gray-800 tw-m-0 tw-mb-1 tw-leading-tight">
                        {name}
                    </h3>
                    <p className="tw-text-sm tw-text-gray-600 tw-m-0 tw-leading-tight">{company}</p>
                </div>
            </div>

            {hasSocialLinks && (
                <>
                    <div className="tw-text-center tw-mb-5">
                        <p className="tw-text-sm tw-text-gray-800 tw-m-0 tw-leading-relaxed">
                            {cta || `Follow ${name.split(" ")[0]} for more exceptional homes`}
                        </p>
                    </div>

                    <div className="tw-flex tw-justify-center tw-gap-3 tw-flex-wrap">
                        {Object.entries(socialLinks).map(([platform, url]) => {
                            if (!url) return null;

                            const networkMap = {
                                instagram: "instagram",
                                tiktok: "tiktok",
                                twitter: "x",
                                facebook: "facebook",
                                youtube: "youtube",
                            };

                            return (
                                <div
                                    key={platform}
                                    className="social-icon"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleSocialLinkClick(platform, url);
                                    }}
                                >
                                    <SocialIcon
                                        url={url}
                                        network={networkMap[platform]}
                                        fgColor=""
                                        bgColor=""
                                        style={{ width: 40, height: 40 }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

AgentCard.propTypes = {
    agent: PropTypes.shape({
        name: PropTypes.string,
        company: PropTypes.string,
        profilePicture: PropTypes.string,
        profileUrl: PropTypes.string,
        cta: PropTypes.string,
        mlsId: PropTypes.string,
        socialLinks: PropTypes.shape({
            instagram: PropTypes.string,
            tiktok: PropTypes.string,
            twitter: PropTypes.string,
            facebook: PropTypes.string,
            youtube: PropTypes.string,
        }),
    }),
    clickLocation: PropTypes.oneOf(["property_page", "daily_score_modal"]).isRequired,
};

export default AgentCard;
